import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationStack } from '../context/NavigationStackContext';
import { useUser } from '../context/UserContext';
import { IMatter } from '../interface/matter';
import { IAPIError, ParameterEntityType } from '../interface/common';
import { AlertType } from '../interface/parameters';
import { PreliminaryInvoiceCreationRequest } from '../interface/invoice';
import { showErrorMessage } from '../constants/preliminaryInvoiceCreationErrorHelpe';
import CustomModal from '../components/informationPopup/customModal';
import { useFetchUserParameters } from './useFetchUserParameters';
import { useFetchMatterParameters } from './useFetchMatterParameters';
import { useCreatePreliminaryInvoice } from './useCreatePreliminaryInvoice';
import { useGetMatterCustomerIdentificationRestriction } from './useGetMatterCustomerIdentificationRestriction';

export const usePreliminaryInvoiceCreation = (matter: IMatter | undefined) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { goBack } = useNavigationStack();
  const { data: featuresInUse } = useFetchUserParameters({
    parametersToGet: ParameterEntityType.FEATURES_IN_USE,
    enabled: !!user?.id,
  });
  const [pendingRequest, setPendingRequest] = useState<PreliminaryInvoiceCreationRequest>();
  const [warnedRequest, setWarnedRequest] = useState<PreliminaryInvoiceCreationRequest>();
  const [isError, setIsError] = useState(false);
  const [message, setMessage] = useState('');

  const createPreliminaryInvoiceMutation = useCreatePreliminaryInvoice();

  const { data: matterParameters } = useFetchMatterParameters({
    matterId: matter?.id,
    enabled:
      !!matter?.id &&
      !!featuresInUse?.parameters.featuresInUseParameters.customerIdentificationInUse,
    parametersToGet: [ParameterEntityType.CUSTOMER_IDENTIFICATION],
  });

  const { customerIdentificationRestriction } = useGetMatterCustomerIdentificationRestriction({
    matterId: matter?.id,
    matterCustomerIdentificationStatus: matter?.customerIdentificationStatus,
    customerIdentificationParametersAlertType:
      matterParameters?.parameters?.customerIdentificationParameters?.preliminaryInvoiceAlertType,
    parameters: featuresInUse,
  });

  const createPreliminaryInvoice = (request: PreliminaryInvoiceCreationRequest) => {
    if (!matter) return;

    createPreliminaryInvoiceMutation.mutate(
      { id: matter.id, ...request },
      {
        onSuccess: () => goBack(),
        onError: (error) => {
          const responseData = error.response?.data as IAPIError;
          if (responseData) {
            showErrorMessage(responseData.type, true, setIsError, setMessage, t);
          }
        },
      },
    );
  };

  useEffect(() => {
    if (!pendingRequest || !customerIdentificationRestriction) return;

    setPendingRequest(undefined);
    const identificationMessage = t('matter:requiredPartyIdentificationHaveNotBeenPerformed');

    switch (customerIdentificationRestriction.customerIdentificationRestrictionType) {
      case AlertType.ERROR:
        setMessage(identificationMessage);
        setIsError(true);
        break;
      case AlertType.WARNING:
        setMessage(`${identificationMessage} ${t('matter:continueToCreateNewPreliminaryInvoice')}`);
        setWarnedRequest(pendingRequest);
        break;
      default:
        createPreliminaryInvoice(pendingRequest);
    }
  }, [pendingRequest, customerIdentificationRestriction]);

  const confirmWarnedRequest = () => {
    if (!warnedRequest) return;

    setWarnedRequest(undefined);
    createPreliminaryInvoice(warnedRequest);
  };

  const CreationModals = (
    <>
      <CustomModal
        visible={!!warnedRequest}
        title={message}
        confirmText={t('common:continue')}
        cancelText={t('common:cancel')}
        onConfirm={confirmWarnedRequest}
        onClose={() => setWarnedRequest(undefined)}
      />
      <CustomModal
        visible={isError}
        title={message}
        confirmText={t('common:cancel')}
        onConfirm={() => setIsError(false)}
      />
    </>
  );

  return { requestCreation: setPendingRequest, CreationModals };
};
